/* eslint-disable no-console */
import { decOpacityToHex, poiInfoToJson, setOpacity } from "./utils";
import { IPoiData } from "./interfaces";
import { deferPromise } from "~/util/general";
import { IXYPosition, TWasmBaseMaps, TWasmId, TWasmMapVariant, TWasmOverlay } from "~/interfaces";
import WasmMapMarker from "./entities/wasm-map-marker";
import WasmMapGeometry from "./entities/wasm-map-geometry";
import { IWasmStoreGeometry, IWasmStoreMarker } from "./store/interfaces";
import { TStoreEventHoverType } from "./store/events";
import { debounce } from "~/lib/debounce";

export type TWasmMapClick = {
	name: "map";
	event: PointerEvent;
} | {
	name: "element";
	wasmId: TWasmId;
} | {
	name: "pois";
	pois: Array<IPoiData>;
};

interface IWasmOpts {
	parentElem: HTMLElement;
	canvasQuery?: string;
	urlReplacement?: {
		fromUrl: string;
		toUrl: string;
	};
	moveTo?: number;
	loadTo?: number;
	urls?: {
		poiAdmin: string;
		styles: string;
	};
	textures?: {
		bigRedPoint: string;
		bigGrayPoint: string;
	};
	clickDistance?: number;
	onMapClick?: (data: TWasmMapClick) => void;
	onHover?: (id: TWasmId, type: TStoreEventHoverType) => void;
	onContextMenu?: (evt: PointerEvent) => void;
	onMapRedraw?: () => void;
	onFps?: (fps: number) => void;
}

const DEF_HOVER_ID = -1;
const CUSTOM_TEXTURES_NAME = {
	custom: "texture://my-texture-1",
	custom2: "texture://my-texture-2",
};
const DROP_HIGH_PRIORITY_LAYER = 1;
const LAYER_MAPPING: {[key in TWasmBaseMaps]: string } = {
	base: "web_a_base",
	turist: "web_b_tourist",
	winter: "web_c_winter",
	ophoto: "web_d_ophoto",
	traffic: "web_traffic",
};
const ALL_OVERLAYS: Array<TWasmOverlay> = ["hillshade", "hillslope", "knLayer", "panoLayer", "touristMode"];


export default class Wasm {
	_opts: IWasmOpts;

	_module: typeof Module;

	_mapControl: Module.MapControl;

	_mapContent: Module.ContentService;

	_poiAdmin: Module.PoiAdminCache;

	_canvas: HTMLCanvasElement;

	_bindUpdateMapContent: () => void;

	_icons: {
		dropBigRedWithIcon: number;
		dropBigGrayWithIcon: number;
		custom: number;
		custom2: number;
	};

	_lineStyles: {
		blue: Module.LineStyle;
		red: Module.LineStyle;
		green: Module.LineStyle;
		route: Module.LineStyle;
		measureLineFinal: Module.LineStyle;
		measureLineCurrent: Module.LineStyle;
		backpackPathActive: Module.LineStyle;
		backpackPathDeactive: Module.LineStyle;
		activityDetail: Module.LineStyle;
		poiDetailGeom: Module.LineStyle;
		routeMhdDefault: Module.LineStyle;
		routeMhdActive: Module.LineStyle;
		routeMhdByWalk: Module.LineStyle;
		routeWalkBg: Module.LineStyle;
		routeWalkOver: Module.LineStyle;
		routeAlternative: Module.LineStyle;
	};

	_loadData: {
		to: number;
	} & ReturnType<typeof deferPromise>;

	_initProm: ReturnType<typeof deferPromise>;

	_lockMap: boolean;

	_screenshotPromise: null | ReturnType<typeof deferPromise<string>>;

	_activePointers: Map<number, PointerEvent>;

	_lastHoverElemWasmId: number;

	_lastPos: IXYPosition;

	_moveState: boolean;

	constructor(opts?: IWasmOpts) {
		this._opts = {
			parentElem: null,
			canvasQuery: "canvas",
			loadTo: 333,
			moveTo: 50,
			urls: {
				poiAdmin: "https://vectmap.mapy.cz/rpc/",
				styles: "/styles/",
			},
			textures: {
				bigRedPoint: "/wasm/drop/Kapka big red.png",
				bigGrayPoint: "/wasm/drop/Kapka big gray.png",
			},
			clickDistance: 3,
			onMapClick: () => {},
			onMapRedraw: () => {},
			onFps: () => {},
			...opts,
		};
		this._mapControl = null;
		this._canvas = document.querySelector(this._opts.canvasQuery);
		this._bindUpdateMapContent = this._updateMapContent.bind(this);
		// tady je to pouze ukazatel, nevraci se to a nenastavuje se to
		this._icons = {
			dropBigRedWithIcon: 0,
			dropBigGrayWithIcon: 3,
			custom: 1,
			custom2: 2,
		};
		this._loadData = {
			to: 0,
			...deferPromise(),
		};
		this._initProm = deferPromise();
		this._lockMap = false;
		this._screenshotPromise = null;
		this._activePointers = new Map();
		this._lastHoverElemWasmId = DEF_HOVER_ID;
		this._lastPos = {
			x: 0,
			y: 0,
		};
		this._moveState = false;

		this._load();
	}

	async _load() {
		const libSznMaps = (await import(/* webpackChunkName: "wasm-lib" */ "../../wasm/lib-maps.js")).default;

		libSznMaps({
			printErr: (...args: any) => {
				if (args.length > 1) {
					console.log(args.join(" "));
				} else {
					console.log(args[0]);
				}
			},
			totalDependencies: 0,
			monitorRunDependencies: (left: number) => {
				if (this._loadData.to) {
					clearTimeout(this._loadData.to);
					this._loadData.to = 0;
				}

				this._loadData.to = setTimeout(() => {
					if (left === 0) {
						this._loadData.resolve({});
					}
				}, this._opts.loadTo) as unknown as number;
			},
			locateFile: (file: string) => `/wasm/${file}`,
		}).then((module: typeof Module) => {
			this._module = module;

			module.setLogLevel(module.LogLevel.None);

			// create poi admin cache, wich resolves poi iconst for map / page
			const poiAdmin = new module.PoiAdminCache(this._opts.urls.poiAdmin);

			// create map
			const mapControl = new module.MapControl(window.devicePixelRatio) as Module.MapControl;

			if (this._opts.urlReplacement) {
				const { fromUrl, toUrl } = this._opts.urlReplacement;
				const mapHostsSetup = new module.MapHostsSetup();

				mapHostsSetup.setUrlReplacement(fromUrl, toUrl, false);
				mapControl.setMapHostsSetup(mapHostsSetup);
			}

			this._mapControl = mapControl;
			this._poiAdmin = poiAdmin;

			const moveCb = debounce(() => {
				if (this._moveState) {
					this._moveState = false;
				} else {
					this._opts.onMapRedraw();
				}
			}, this._opts.moveTo);

			mapControl.setOnMapViewChanged(() => {
				moveCb();
			});

			// setup poi icon resolver used by map - poiAdmin class
			mapControl.setCustomPoiIconResolver(poiAdmin);

			// initialize map cotent service
			this._initMapContentService(mapControl.getContentService());

			// load styles from server path & show them in console, select default stype set after load
			mapControl.loadStyles(this._opts.urls.styles, availableStyles => {
				this._printAvailableStyles(availableStyles);
			});

			// connect map to canvas & update size
			mapControl.connectToCanvas(this._opts.canvasQuery);

			this._canvas.tabIndex = -1;
			this._canvas.addEventListener("contextmenu", event => {
				event.preventDefault();
			});

			// update size of map when resise
			this._canvas.addEventListener("resize", () => {
				this._updateMapControlSize();
			});

			// update size of map now
			this._updateMapControlSize();

			// start render loop
			window.requestAnimationFrame(this._bindUpdateMapContent);

			mapControl.setOnRenderStats(stats => {
				this._opts.onFps(stats.fps);
			});

			// send mouse events to map
			this._initMoveControls();

			this._initProm.resolve({});
		});
	}

	// vsechny exporty v jednom
	get exports() {
		return {
			module: this._module,
			mapControl: this._mapControl,
			mapContent: this._mapContent,
			poiAdmin: this._poiAdmin,
			icons: this._icons,
			lineStyles: this._lineStyles,
		};
	}

	get loadProm() {
		return Promise.all([
			this._loadData.promise,
			this._initProm.promise,
		]);
	}

	set lockMap(lockMap: boolean) {
		this._lockMap = lockMap;
	}

	syncPort() {
		this._updateMapControlSize();
	}

	createMarker(markerData: IWasmStoreMarker) {
		return new WasmMapMarker(this, markerData);
	}

	createGeometry(geomData: IWasmStoreGeometry) {
		return new WasmMapGeometry(this, geomData);
	}

	setCustomTexture(type: "custom" | "custom2", imageData: ImageData) {
		switch (type) {
			case "custom":
				this._mapContent.createOrUpdateTexture(CUSTOM_TEXTURES_NAME.custom, imageData);
				break;

			case "custom2":
				this._mapContent.createOrUpdateTexture(CUSTOM_TEXTURES_NAME.custom2, imageData);
				break;

			default:
		}
	}

	setMapstyle(base: TWasmBaseMaps, overlays: Array<TWasmOverlay> = [], variant: TWasmMapVariant = "") {
		const wasmName = LAYER_MAPPING[base];
		const styleSet = new this._module.StyleSetCfg(wasmName, variant);

		// set styleset attributes values
		for (const overlay of ALL_OVERLAYS) {
			styleSet.setAttribute(overlay, overlays.includes(overlay) ? "true" : "false");
		}

		// set styleset config to map
		this._mapControl.setStyleSet(styleSet);

		// delete after use
		styleSet.delete();
	}

	getScreenshot() {
		this._screenshotPromise = deferPromise<string>();
		this._mapControl.requestFrame();

		return this._screenshotPromise.promise;
	}

	setCenterZoom(wgsX: number, wgsY: number, zoom?: number) {
		this._moveState = true;
		this._mapControl.setPositionWgs({
			x: wgsX,
			y: wgsY,
		});
		!isNaN(zoom) && this._mapControl.setZoom(zoom);
	}

	getCenterZoom() {
		const coords = this._mapControl.getPositionWgs();
		const zoom = this._mapControl.getZoom();

		return {
			lon: coords.x,
			lat: coords.y,
			zoom,
		};
	}

	_initMapContentService(contentService: Module.ContentService) {
		// setup global variable
		this._mapContent = contentService;

		this._registerIcons();
		this._registerLineStyles();

		contentService.setRequestDataCallback(async requestedUrl => {
			// todor - doplnieme nacitani custom textur
			console.log(requestedUrl);
			const customRes = false;

			// funstion must download data and set it back to conent service
			if (requestedUrl.includes("/img/")) {
				// fetch data
				const response = await fetch(requestedUrl);
				const data = await response.arrayBuffer();

				// set data back to conent
				contentService.setRequestedData(requestedUrl, new Uint8Array(data));
			} else if (requestedUrl.includes("api.mapy.") && customRes) {
				const canvas = document.createElement("canvas");

				canvas.width = 100;
				canvas.height = 100;

				const ctx = canvas.getContext("2d");

				ctx.fillStyle = "red";
				ctx.fillRect(0, 0, 100, 100);

				//const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);

				canvas.toBlob(blob => {
					const reader = new FileReader();

					reader.addEventListener("loadend", () => {
						const arrayBuffer = reader.result;

						contentService.setRequestedData(requestedUrl, new Uint8Array(arrayBuffer as ArrayBuffer));
					});
					reader.readAsArrayBuffer(blob);
				}, "image/png");
			}
		});

		contentService.setOnClickCallback(clickData => {
			if (!clickData) {
				// click do mapy - nic tam neni
				return;
			}

			if (clickData.DataType === this._module.ActionDataType.Poi) {
				const pois = clickData.Pois;
				const poisData: Array<IPoiData> = [];

				for (let ind = 0; ind < pois.size(); ind++) {
					const selPoiInfo = pois.get(ind);

					poisData.push(poiInfoToJson(selPoiInfo));
				}

				this._opts.onMapClick({
					name: "pois",
					pois: poisData,
				});
			} else if (clickData.DataType === this._module.ActionDataType.MapElement) {
				this._opts.onMapClick({
					name: "element",
					wasmId: clickData.ElementId,
				});
				//console.log(`Clicked on ${clickData.ElementType === this._module.ElementType.Point ? "Point" : "Line"}, object id = ${clickData.ElementId}, contact pos [${clickData.ContactPos.x}; ${clickData.ContactPos.y}], userData = ${JSON.stringify(clickData.UserData, null, 4)})`);
			}

			// smazeme data -> kvuli infu do konzole
			clickData.delete();
		});

		contentService.setOnHoverCallback(hoverData => {
			let newHoverElemWasmId = 0;

			if (hoverData === null) {
				newHoverElemWasmId = DEF_HOVER_ID;
			} else if (hoverData.DataType === this._module.ActionDataType.Poi) {
				// hover on map poi
				newHoverElemWasmId = DEF_HOVER_ID;
			} else if (hoverData.DataType === this._module.ActionDataType.MapElement) {
				// hover on map data element
				newHoverElemWasmId = hoverData.ElementId;
				//console.log(`Hovered on ${hoverData.ElementType === this._module.ElementType.Point ? "Point" : "Line"}, object id = ${hoverData.ElementId}, contact pos [${hoverData.ContactPos.x}; ${hoverData.ContactPos.y}], userData = ${JSON.stringify(hoverData.UserData, null, 4)})`);
			}

			if (this._lastHoverElemWasmId === DEF_HOVER_ID) {
				if (newHoverElemWasmId !== DEF_HOVER_ID) {
					this._opts.onHover(newHoverElemWasmId, "enter");
				}
			} else if (this._lastHoverElemWasmId !== newHoverElemWasmId) {
				// predtim bylo, tedka prazdno
				if (newHoverElemWasmId === DEF_HOVER_ID) {
					this._opts.onHover(this._lastHoverElemWasmId, "leave");
				} else {
					// najeli jsme na jiny
					this._opts.onHover(this._lastHoverElemWasmId, "leave");
					this._opts.onHover(newHoverElemWasmId, "enter");
				}
			}

			this._lastHoverElemWasmId = newHoverElemWasmId;

			if (hoverData) {
				// smazeme data -> kvuli infu do konzole
				hoverData.delete();
			}
		});
	}

	_printAvailableStyles(availableStyles: Module.IAvailableStyles) {
		/* eslint-disable */
		for (let ind = 0; ind < availableStyles.size(); ind++) {
			const style = availableStyles.get(ind);

			// style id and its localised names for cs & en
			let msg = `Style id = '${style.getId()}', localised names (cs) = '${style.getName("cs")}', (en) = '${style.getName("en")}'`;

			// style variants - "empty string" variant id is always default variant
			const variantIds = style.getVariantIds();

			for (let jInd = 0; jInd < variantIds.size(); ++jInd) {
				const variantId = variantIds.get(jInd);

				msg += `\n   variantId = '${variantId}', attributes = [`;

				// list variant attributes
				const variantAttributes = style.getVariantAttributes(variantId);

				for (let kInd = 0; kInd < variantAttributes.size(); ++kInd) {
					if (kInd > 0) {
						msg += ", ";
					}

					msg += variantAttributes.get(kInd);
				}

				msg += "]";
			}

			/* eslint-disable-next-line */
			//console.log(msg);
		}

		this._mapControl.setDefaultStyleSet();
	}

	_updateMapControlSize() {
		const dpiScale = window.devicePixelRatio;
		const width = this._opts.parentElem.offsetWidth;
		const height = this._opts.parentElem.offsetHeight;

		this._canvas.style.width = `${width}px`;
		this._canvas.style.height = `${height}px`;
		this._canvas.width = width * dpiScale;
		this._canvas.height = height * dpiScale;

		console.debug(`Canvas width ${this._canvas.width} height ${this._canvas.height}`);

		if (this._mapControl) {
			this._mapControl.setHostSize(width, height);
			this._mapControl.setRenderResolution(this._canvas.width, this._canvas.height);
			window.requestAnimationFrame(this._bindUpdateMapContent);
		}
	}

	_updateMapContent(timestamp: number) {
		if (this._mapControl) {
			this._mapControl.update(timestamp);

			if (this._screenshotPromise) {
				const imgSrcData = this._canvas.toDataURL();

				this._screenshotPromise.resolve(imgSrcData);
				this._screenshotPromise = null;
			}

			window.requestAnimationFrame(this._bindUpdateMapContent);
		}
	}

	_eventDown(evt: PointerEvent) {
		if (this._mapControl && !this._lockMap) {
			this._mapControl.onPointerDown(evt.pointerId, evt.clientX, evt.clientY);
		}

		this._activePointers.set(evt.pointerId, evt);
	}

	_eventMove(evt: PointerEvent) {
		if (this._mapControl && !this._lockMap) {
			if (this._activePointers.has(evt.pointerId)) {
				this._mapControl.onPointerMove(evt.pointerId, evt.clientX, evt.clientY);
			} else if (this._activePointers.size === 0 && evt.pointerType === "mouse") {
				// send hover to map
				this._mapControl?.onHover(evt.clientX, evt.clientY);
			}
		}
	}

	_eventUp(evt: PointerEvent) {
		if (this._mapControl && !this._lockMap) {
			this._mapControl.onPointerUp(evt.pointerId, evt.clientX, evt.clientY);
		}

		if (evt.pointerType === "mouse" && evt.button === 2) {
			this._opts.onContextMenu(evt);
		}

		if (this._activePointers.has(evt.pointerId) && this._activePointers.size === 1) {
			const origEvent = this._activePointers.get(evt.pointerId);
			const dist = Math.sqrt(Math.pow(evt.clientX - origEvent.clientX, 2) + Math.pow(evt.clientY - origEvent.clientY, 2));

			if (dist <= this._opts.clickDistance) {
				this._opts.onMapClick({
					name: "map",
					event: evt,
				});
			}
		}

		this._activePointers.delete(evt.pointerId);
	}

	_eventCancel(evt: PointerEvent) {
		if (this._mapControl && !this._lockMap) {
			this._mapControl.onPointerCancel(evt.pointerId);
		}

		this._activePointers.delete(evt.pointerId);
	}

	_initMoveControls() {
		const elem = this._opts.parentElem;

		// web
		elem.addEventListener("pointerdown", event => {
			this._eventDown(event);
		});
		elem.addEventListener("pointermove", event => {
			this._eventMove(event);
		});
		elem.addEventListener("pointerup", event => {
			this._eventUp(event);
		});
		elem.addEventListener("pointercancel", event => {
			this._eventCancel(event);
		});

		// kolecko
		elem.addEventListener("wheel", evt => {
			const divider = evt.deltaMode == 1
				/* deltaY is in pages */
				? 12
				/* deltaY is in pixels */
				: 400.0;

			this._mapControl?.onMouseWheel(evt.offsetX, evt.offsetY, evt.deltaY / divider);

			// pinch zoom detected by Control key press -> do not zoom page
			if (evt.ctrlKey) {
				evt.preventDefault();
			}
		}, false);
	}

	_registerIcons() {
		// register big drop element with dynamic visual part
		this._mapContent.registerPointElementVisual(this._icons.dropBigRedWithIcon, [
			new this._module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, 75, 66, 23.5, 65.5, this._opts.textures.bigRedPoint),
			new this._module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, 30, 30, 15, 57, (elementId, userData) => {
				if (userData) {
					return userData.url;
				}

				return "";
			}),
		]);
		this._mapContent.registerPointElementVisual(this._icons.dropBigGrayWithIcon, [
			new this._module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, 75, 66, 23.5, 65.5, this._opts.textures.bigGrayPoint),
			new this._module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, 30, 30, 15, 57, (elementId, userData) => {
				if (userData) {
					return userData.url;
				}

				return "";
			}),
		]);
		this._mapContent.registerPointElementVisual(this._icons.custom, [
			new this._module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, 75, 66, 23.5, 65.5, CUSTOM_TEXTURES_NAME.custom),
		]);
		this._mapContent.registerPointElementVisual(this._icons.custom2, [
			new this._module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, 75, 66, 23.5, 65.5, CUSTOM_TEXTURES_NAME.custom2),
		]);
	}

	_registerLineStyles() {
		const finalStyles = {
			measureLineFinal: new this._module.LineStyle(
				8,
				"#ff0000",
				0,
				"#ff0000",
				false,
				this._module.LineCapType.Square,       // line ends type (Butt, Round, Square)
				this._module.LineJointType.Round,     // line joint style - joint between line segments (Bevel, Miter, Round)
				""                  // sroke-dash-stroke-dash lengths
			),
			measureLineCurrent: new this._module.LineStyle(
				8,
				"#ff0000",
				0,
				"#ff0000",
				false,
				this._module.LineCapType.Round,       // line ends type (Butt, Round, Square)
				this._module.LineJointType.Round,     // line joint style - joint between line segments (Bevel, Miter, Round)
				"20,20,20,20"                  // sroke-dash-stroke-dash lengths
			),
			backpackPathActive: new this._module.LineStyle(
				// stroke opacity 0.65 -> (0.65*255 >>> 0).toString(16) = a5
				8,
				`#ff0000${decOpacityToHex(0.65)}`,
				1,
				"#ffffff",
				false,
				this._module.LineCapType.Square,       // line ends type (Butt, Round, Square)
				this._module.LineJointType.Round,     // line joint style - joint between line segments (Bevel, Miter, Round)
				""                  // sroke-dash-stroke-dash lengths
			),
			backpackPathDeactive: new this._module.LineStyle(
				8,
				"#6b7580",
				1,
				"#ffffff",
				false,
				this._module.LineCapType.Square,       // line ends type (Butt, Round, Square)
				this._module.LineJointType.Round,     // line joint style - joint between line segments (Bevel, Miter, Round)
				""                  // sroke-dash-stroke-dash lengths
			),
			activityDetail: new this._module.LineStyle(
				12,
				`#ff0000${decOpacityToHex(0.65)}`,
				1,
				"#ffffff",
				false,
				this._module.LineCapType.Square,       // line ends type (Butt, Round, Square)
				this._module.LineJointType.Round,     // line joint style - joint between line segments (Bevel, Miter, Round)
				""                  // sroke-dash-stroke-dash lengths
			),
			poiDetailGeom: new this._module.LineStyle(
				6,
				"#ff0000",
				4,
				"#ffffff",
				false,
				this._module.LineCapType.Square,       // line ends type (Butt, Round, Square)
				this._module.LineJointType.Round,     // line joint style - joint between line segments (Bevel, Miter, Round)
				""                  // sroke-dash-stroke-dash lengths
			),
			routeMhdDefault: new this._module.LineStyle(
				12,
				`#1263b5${decOpacityToHex(0.8)}`,
				4,
				`#ffffff${decOpacityToHex(0.6)}`,
				false,
				this._module.LineCapType.Square,       // line ends type (Butt, Round, Square)
				this._module.LineJointType.Round,     // line joint style - joint between line segments (Bevel, Miter, Round)
				""                  // sroke-dash-stroke-dash lengths
			),
			routeMhdActive: new this._module.LineStyle(
				12,
				`#cc0011${decOpacityToHex(1)}`,
				4,
				`#ffffff${decOpacityToHex(0.6)}`,
				false,
				this._module.LineCapType.Square,       // line ends type (Butt, Round, Square)
				this._module.LineJointType.Round,     // line joint style - joint between line segments (Bevel, Miter, Round)
				""                  // sroke-dash-stroke-dash lengths
			),
			// chybi bile pozadi
			routeMhdByWalk: new this._module.LineStyle(
				12,
				`#454c54${decOpacityToHex(0.8)}`,
				4,
				`#ffffff${decOpacityToHex(0.6)}`,
				false,
				this._module.LineCapType.Square,       // line ends type (Butt, Round, Square)
				this._module.LineJointType.Bevel,     // line joint style - joint between line segments (Bevel, Miter, Round)
				"20,20,20,20"                  // sroke-dash-stroke-dash lengths
			),
			// nasledujici dva styly musi jit vedle sebe, jinak bila cerchovana cara neni spojita
			routeWalkBg: new this._module.LineStyle(
				12,
				`#ffffff${decOpacityToHex(0.6)}`,
				0,
				`#ffffff`,
				false,
				this._module.LineCapType.Square,       // line ends type (Butt, Round, Square)
				this._module.LineJointType.Bevel,     // line joint style - joint between line segments (Bevel, Miter, Round)
				""                  // sroke-dash-stroke-dash lengths
			),
			routeWalkOver: new this._module.LineStyle(
				12,
				`#0033ff${decOpacityToHex(0.6)}`,
				0,
				`#ffffff`,
				false,
				this._module.LineCapType.Square,       // line ends type (Butt, Round, Square)
				this._module.LineJointType.Bevel,     // line joint style - joint between line segments (Bevel, Miter, Round)
				// orig 14,10
				"14,14,14,14"                  // sroke-dash-stroke-dash lengths
			),
			routeAlternative: new this._module.LineStyle(
				12,
				`#454c54${decOpacityToHex(0.6)}`,
				2,
				`#ffffff`,
				false,
				this._module.LineCapType.Square,       // line ends type (Butt, Round, Square)
				this._module.LineJointType.Bevel,     // line joint style - joint between line segments (Bevel, Miter, Round)
				""                  // sroke-dash-stroke-dash lengths
			),
		};

		const blue = new this._module.LineStyle(
			8,                              // swroke width
			"#03A9F4",                      // stroke color
			2,                              // line border width
			"#156B91",                      // border color
			true,                           // keep line with between zooms:
											// this parameter defines how is line width threated (keepExactWidth)
											// keepExactWidth = true - pixel width of line is exactly keept also between map zooms (line has always the same width)
											// keepExactWidth = false - line width is scaled as map content on current map zoom
			this._module.LineCapType.Round,       // line ends type (Butt, Round, Square)
			this._module.LineJointType.Round,     // line joint style - joint between line segments (Bevel, Miter, Round)
			""
		);
		const green = new this._module.LineStyle(
			8,
			"#ff0000",
			0,
			"#ff0000",
			false,
			this._module.LineCapType.Square,       // line ends type (Butt, Round, Square)
			this._module.LineJointType.Round,     // line joint style - joint between line segments (Bevel, Miter, Round)
			""                  // sroke-dash-stroke-dash lengths
		);
		const red = new this._module.LineStyle(
			6,                              // swroke width
			"#ff0000",                      // stroke color
			2,                              // line border width
			"#ffffff",                      // border color
			true,                           // keep line with between zooms:
											// this parameter defines how is line width threated (keepExactWidth)
											// keepExactWidth = true - pixel width of line is exactly keept also between map zooms (line has always the same width)
											// keepExactWidth = false - line width is scaled as map content on current map zoom
			this._module.LineCapType.Round,       // line ends type (Butt, Round, Square)
			this._module.LineJointType.Round,     // line joint style - joint between line segments (Bevel, Miter, Round)
			""
		);

		/**
		 * width: 6,
		color: "#0033ff",
		opacity: 0.6,
		outlineWidth: 2,
		outlineColor: "white",
		outlineOpacity: 0.4,
		style: JAK.Vector.STYLE_DASH,
		endCap: JAK.Vector.END_STYLE_BUTT,
		exactStyle: "14px 10px",
		 */
		const route = new this._module.LineStyle(
			// pro mapy.com sirky x2
			16,                              // swroke width
			//setOpacity("#0033ff", 0.6),                      // stroke color
			setOpacity("#0033ff", 0.6),                      // stroke color
			4,                              // line border width
			setOpacity("#ffffff", 0.4),                      // border color
			true,                           // keep line with between zooms:
											// this parameter defines how is line width threated (keepExactWidth)
											// keepExactWidth = true - pixel width of line is exactly keept also between map zooms (line has always the same width)
											// keepExactWidth = false - line width is scaled as map content on current map zoom
			this._module.LineCapType.Butt,       // line ends type (Butt, Round, Square)
			this._module.LineJointType.Round,     // line joint style - joint between line segments (Bevel, Miter, Round)
			""
		);

		this._lineStyles = {
			blue,
			green,
			red,
			route,
			...finalStyles,
		};
	}
}
